import type {DsItem, Pointer} from './basicTypes'
import type {UnitSize, Alignment, JustifyContent, Position} from './layoutData'

export interface VariablesManifest {
    schemaNameConfig?: {
        [key: string]: {
            useSchemasFromDefinitions: boolean
            useAbsoluteUrn: boolean
        }
    }
    variables: {
        [namespace: string]: {
            [dataType: string]: {
                variableName: string
                variableValueName: string
            }
        }
    }
    schemasThatUseVariables?: {
        [namespace: string]: {
            [dataType: string]: string
        }
    }
}

export type StyleVariableType =
    | 'NumVar'
    | 'LengthVar'
    | 'AngleVar'
    | 'ColorVar'
    | 'PercentageVar'
    | 'BorderEdgeStyleVar'
    | 'BorderEdgeWidthVar'
    | 'BorderWidthVar'
    | 'BorderStyleVar'
    | 'BorderColorVar'
    | 'BorderVar'
    | 'BorderCornerRadiusVar'
    | 'BorderRadiusVar'
    | 'FontVar'
    | 'FontSizeVar'
    | 'FontStretchVar'
    | 'FontStyleVar'
    | 'FontVariantVar'
    | 'FontWeightVar'
    | 'FontFamilyVar'
    | 'LineHeightVar'
    | 'LetterSpacingVar'
    | 'TextAlignVar'
    | 'TextTransformVar'
    | 'TextShadowVar'
    | 'TextDecorationVar'
    | 'TextDecorationLineVar'
    | 'TextDecorationStyleVar'
    | 'TextDecorationThicknessVar'
    | 'PaddingVar'
    | 'PaddingEdgeVar'
    | 'MarginVar'
    | 'MarginEdgeVar'
    | 'BoxShadowVar'
    | 'OpacityVar'
    | 'CssJustifyContentVar'
    | 'BackgroundVar'
    | 'WritingModeVar'
    | 'DisplayVar'
    | 'FilterVar'
    | 'GapVar'
    | 'SingleValueGapVar'

export type LayoutVariableType =
    | 'UnitSizeVar'
    | 'NumberVar'
    | 'JustifyContentVar'
    | 'ItemsAlignmentVar'
    | 'PositionTypeVar'

export type VariableType = LayoutVariableType | StyleVariableType

export type StyleVariableValueType =
    | 'NumValue'
    | 'LengthValue'
    | 'AngleValue'
    | 'ColorValue'
    | 'PercentageValue'
    | 'BorderEdgeStyleValue'
    | 'BorderEdgeWidthValue'
    | 'BorderWidthValue'
    | 'BorderStyleValue'
    | 'BorderColorValue'
    | 'BorderValue'
    | 'BorderCornerRadiusValue'
    | 'BorderRadiusValue'
    | 'FontValue'
    | 'FontSizeValue'
    | 'FontStretchValue'
    | 'FontStyleValue'
    | 'FontVariantValue'
    | 'FontWeightValue'
    | 'FontFamilyValue'
    | 'LineHeightValue'
    | 'LetterSpacingValue'
    | 'TextAlignValue'
    | 'TextTransformValue'
    | 'TextShadowValue'
    | 'TextDecorationValue'
    | 'TextDecorationLineValue'
    | 'TextDecorationStyleValue'
    | 'TextDecorationThicknessValue'
    | 'PaddingValue'
    | 'PaddingEdgeValue'
    | 'MarginValue'
    | 'MarginEdgeValue'
    | 'BoxShadowValue'
    | 'OpacityValue'
    | 'CssJustifyContentValue'
    | 'BackgroundValue'
    | 'WritingModeValue'
    | 'DisplayValue'
    | 'FilterValue'
    | 'GapValue'
    | 'SingleValueGapValue'

export type LayoutVariableValueType =
    | 'UnitSizeValue'
    | 'NumberValue'
    | 'JustifyContentValue'
    | 'ItemsAlignmentValue'
    | 'PositionTypeValue'

export type VariableValueType = LayoutVariableValueType | StyleVariableValueType

export type StyleVariableValueValue = string

export type LayoutVariableValueValue = UnitSize | number | JustifyContent | Alignment | Position

export type VariableValueValue = LayoutVariableValueValue | StyleVariableValueValue

export interface VariableValue {
    type: VariableValueType
    value: VariableValueValue
}

export interface VariableData {
    name?: string
    cssVarName?: string
    type: VariableType
    value: VariableValue
}

export interface Variable {
    name?: string
    value: string
}

export interface VariablesList extends DsItem {
    variables: Variable[]
}

export interface VariablePointer extends Pointer {
    type: 'variables'
}

export interface VariableConnections {
    [path: string]: VariablePointer
}

export enum VariableReferenceNamespace {
    CSS_STYLE = 'cssStyle',
    LAYOUT = 'layout',
    VARIABLES = 'variables'
}

export interface VariableReferenceDescription {
    pointer: Pointer
    namespace: VariableReferenceNamespace
    propertyPaths: string[][]
    innerElementPath?: string[]
}

export interface PointerWithVariations {
    pointer: Pointer
    variations: VariableReferenceDescription[]
}

export interface VariableReferences {
    structure: Pointer[]
    style: Pointer[]
    variables: Pointer[]
}

export interface GlobalVariableReferences {
    style?: PointerWithVariations[]
    variables?: PointerWithVariations[]
}

export interface VariableReferencesV2 {
    components: PointerWithVariations[]
    global: GlobalVariableReferences
}

export interface RawVariableConnection {
    path: string
    variableId: string
}

export interface ParentsVariablesNode {
    pointer: VariablePointer
    parents: ParentsVariablesNode[]
}

export interface ChildrenVariablesNode {
    pointer: VariablePointer
    children: ChildrenVariablesNode[]
}

export interface SerializedVariableValue {
    type: string
    value: VariableValueValue
    variableConnections: Record<string, string>
}
